// Create an order by hand from the Orders page.
//
// Same two-column shape as the order-detail modal — lines on the left,
// order fields + address form on the right — so an admin who edits orders
// already knows where everything is. Nothing is written until Create
// order: lines are held locally and sent with the header in ONE request
// (POST /orders), so a half-built order can never be left behind.
//
// The order lands ON HOLD (hold_reason 'manual'): it is reviewed on the
// Orders page and released to picking with the normal status change to
// Unfulfilled. Lines default to cost price. Billing defaults to a copy
// of the delivery address unless the tick is cleared.
import { useMemo, useState } from "react";
import { X, CheckCircle, Package, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import { useWarehouseScope } from "../../access/useWarehouseScope";
import { apiFetch } from "../../api/apiFetch";
import type { DispatchOrder } from "../../types/Dispatch";
import { EU_COUNTRY_OPTIONS, dialCodeForCountry, isValidNationalForDial, EU_PHONE_FORMATS } from "../helpers/phoneFormat";
import OrderProductPicker, { type PickedProduct } from "./order-product-picker";

export type DraftLine = {
    sku: string;
    title: string;
    image_url: string | null;
    quantity: number;
    unit_price: number;
};

export type DraftAddress = {
    name: string;
    company: string;
    line_1: string;
    line_2: string;
    city: string;
    county: string;
    postcode: string;
    country: string;
    phone_country: string;
    phone: string;
    email: string;
};

const emptyAddress = (country = "GB"): DraftAddress => ({
    name: "", company: "", line_1: "", line_2: "", city: "", county: "", postcode: "",
    country, phone_country: dialCodeForCountry(country) || "", phone: "", email: "",
});

// Add a picked product to the draft, merging into an existing line of the
// same SKU (the BE merges too, but showing one line keeps the totals and
// the remove button honest).
export const mergeDraftLine = (lines: DraftLine[], next: DraftLine): DraftLine[] => {
    const idx = lines.findIndex(l => l.sku === next.sku);
    if (idx === -1) return [...lines, next];
    return lines.map((l, i) => (i === idx ? { ...l, quantity: l.quantity + next.quantity } : l));
};

// The fields the BE insists on for a dispatchable delivery address.
export const missingDeliveryFields = (a: DraftAddress): string[] =>
    (["name", "line_1", "city", "postcode", "country"] as const).filter(f => !a[f].trim());

// Address → the shape POST /orders takes (phone digits only; the dial
// code travels separately so the BE trusts it as-is).
export const addressPayload = (a: DraftAddress) => ({
    name: a.name.trim(),
    company: a.company.trim() || null,
    line_1: a.line_1.trim(),
    line_2: a.line_2.trim() || null,
    city: a.city.trim(),
    county: a.county.trim() || null,
    postcode: a.postcode.trim(),
    country: a.country,
    phone_country: a.phone ? (a.phone_country || null) : null,
    phone_number: a.phone ? a.phone.replace(/\D/g, "") : null,
    email: a.email.trim() || null,
});

export const draftTotal = (lines: DraftLine[], shipping: number) =>
    Math.round((lines.reduce((s, l) => s + l.quantity * l.unit_price, 0) + shipping) * 100) / 100;

const formatMoney = (value: number, currency: string) => {
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
};

type AddressFieldsProps = {
    value: DraftAddress;
    onChange: (next: DraftAddress) => void;
    idPrefix: string;
};

// One address block. Mirrors the field layout of the detail modal's
// address form so the two screens read the same.
const AddressFields = ({ value, onChange, idPrefix }: AddressFieldsProps) => {
    const { t } = useTranslation();
    const set = (patch: Partial<DraftAddress>) => onChange({ ...value, ...patch });
    const id = (f: string) => `${idPrefix}-${f}`;
    return (
        <>
            <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("name")}>{t("name")}</label>
                    <input id={id("name")} value={value.name} onChange={e => set({ name: e.target.value })} placeholder={t("name")} />
                </div>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("company")}>{t("company")}</label>
                    <input id={id("company")} value={value.company} onChange={e => set({ company: e.target.value })} placeholder={t("company_optional")} />
                </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("line1")}>{t("address_line_1")}</label>
                    <input id={id("line1")} value={value.line_1} onChange={e => set({ line_1: e.target.value })} placeholder={t("address_line_1")} />
                </div>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("line2")}>{t("address_line_2")}</label>
                    <input id={id("line2")} value={value.line_2} onChange={e => set({ line_2: e.target.value })} placeholder={t("address_line_2")} />
                </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("city")}>{t("city")}</label>
                    <input id={id("city")} value={value.city} onChange={e => set({ city: e.target.value })} placeholder={t("city")} />
                </div>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("county")}>{t("county")}</label>
                    <input id={id("county")} value={value.county} onChange={e => set({ county: e.target.value })} placeholder={t("county")} />
                </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("postcode")}>{t("postcode")}</label>
                    <input id={id("postcode")} value={value.postcode} onChange={e => set({ postcode: e.target.value })} placeholder={t("postcode")} />
                </div>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("country")}>{t("country")}</label>
                    <select
                        id={id("country")}
                        value={value.country}
                        onChange={e => {
                            const next = e.target.value;
                            const dc = dialCodeForCountry(next);
                            set({ country: next, ...(dc ? { phone_country: dc } : {}) });
                        }}
                    >
                        <option value="">—</option>
                        {EU_COUNTRY_OPTIONS.map(c => (
                            <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("phone")}>{t("mobile")}</label>
                    <div className="dispatch-phone-input-group">
                        <select
                            className="dispatch-phone-dial-select"
                            value={value.phone_country}
                            onChange={e => set({ phone_country: e.target.value })}
                            aria-label={t("phone_country_code")}
                            title={t("phone_country_code")}
                        >
                            <option value="">—</option>
                            {EU_COUNTRY_OPTIONS.map(c => (
                                <option key={c.code} value={c.dialCode}>{c.dialCode}</option>
                            ))}
                        </select>
                        <input
                            id={id("phone")}
                            className="dispatch-phone-national"
                            value={value.phone}
                            onChange={e => set({ phone: e.target.value })}
                            placeholder={EU_PHONE_FORMATS[value.country]?.mobileExample || t("mobile")}
                            type="tel"
                            inputMode="numeric"
                        />
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <label className="dispatch-edit-label" htmlFor={id("email")}>{t("email")}</label>
                    <input id={id("email")} value={value.email} onChange={e => set({ email: e.target.value })} placeholder={t("email")} type="email" />
                </div>
            </div>
        </>
    );
};

type Props = {
    warehouses: string[];
    defaultWarehouse?: string;
    onClose: () => void;
    // Fires with the created order (the BE's detail shape) so the Orders
    // page can refresh its list and open it.
    onCreated: (order: DispatchOrder, orderNumber: string) => void;
};

const CreateOrderModal = ({ warehouses: allWarehouses, defaultWarehouse, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    // Without cross_warehouse only the person's own warehouse is offered.
    const { canCross, own } = useWarehouseScope();
    const warehouses = useMemo(() => (canCross ? allWarehouses : allWarehouses.filter(w => w === own)), [canCross, own, allWarehouses]);
    const [warehouse, setWarehouse] = useState(
        defaultWarehouse && warehouses.includes(defaultWarehouse) ? defaultWarehouse : (warehouses[0] ?? ""),
    );
    const [orderNumber, setOrderNumber] = useState("");
    const [deliveryType, setDeliveryType] = useState<"standard" | "priority" | "express" | "collection">("standard");
    const [shipping, setShipping] = useState("0");
    const [notes, setNotes] = useState("");
    const [delivery, setDelivery] = useState<DraftAddress>(() => emptyAddress());
    const [billing, setBilling] = useState<DraftAddress>(() => emptyAddress());
    const [billingSame, setBillingSame] = useState(true);
    const [addressTab, setAddressTab] = useState<"delivery" | "billing">("delivery");
    const [lines, setLines] = useState<DraftLine[]>([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fieldError, setFieldError] = useState<string | null>(null);

    const currency = "GBP";
    const shippingNum = Number(shipping) || 0;
    const total = useMemo(() => draftTotal(lines, shippingNum), [lines, shippingNum]);
    const unitCount = lines.reduce((s, l) => s + l.quantity, 0);

    const addLine = (p: PickedProduct, quantity: number, unitPrice: number) => {
        setLines(prev => mergeDraftLine(prev, {
            sku: p.sku,
            title: p.title || p.sku,
            image_url: p.image_url,
            quantity,
            unit_price: unitPrice,
        }));
        setPickerOpen(false);
    };

    const removeLine = (sku: string) => setLines(prev => prev.filter(l => l.sku !== sku));

    const setLineQty = (sku: string, raw: string) => {
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 1) return;
        setLines(prev => prev.map(l => (l.sku === sku ? { ...l, quantity: n } : l)));
    };

    const validate = (): string | null => {
        if (!warehouse) return t("create_order_missing_warehouse");
        if (missingDeliveryFields(delivery).length > 0) return t("create_order_missing_address");
        if (!billingSame && missingDeliveryFields(billing).length > 0) return t("create_order_missing_billing");
        if (lines.length === 0) return t("create_order_need_lines");
        if (delivery.phone && !isValidNationalForDial(delivery.phone, delivery.phone_country, delivery.country)) {
            return t("phone_invalid_for_country");
        }
        if (!billingSame && billing.phone && !isValidNationalForDial(billing.phone, billing.phone_country, billing.country)) {
            return t("billing_phone_invalid_for_country");
        }
        if (!Number.isFinite(Number(shipping)) || Number(shipping) < 0) return t("create_order_bad_shipping");
        return null;
    };

    const save = async () => {
        if (saving || readOnly) return;
        const problem = validate();
        if (problem) {
            setFieldError(problem);
            toast.error(problem, { toastId: "create-order-invalid" });
            return;
        }
        setFieldError(null);
        setSaving(true);
        try {
            const body = {
                warehouse,
                order_number: orderNumber.trim() || undefined,
                delivery_type: deliveryType,
                currency,
                shipping_paid: shippingNum,
                notes: notes.trim() || undefined,
                delivery_address: addressPayload(delivery),
                billing_address: billingSame ? undefined : addressPayload(billing),
                lines: lines.map(l => ({ sku: l.sku, quantity: l.quantity, unit_price: l.unit_price })),
            };
            const res = await apiFetch(`/orders`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({} as Record<string, unknown>));
            if (!res.ok) {
                const msg = (data?.message as string) || t("create_order_failed");
                setFieldError(msg);
                toast.error(msg, { toastId: "create-order-failed" });
                return;
            }
            onCreated(data.order as DispatchOrder, String(data.order_number ?? ""));
        } catch {
            toast.error(t("create_order_failed"), { toastId: "create-order-failed" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={() => { if (!saving) onClose(); }}>
            <div className="modal-card dispatch-detail-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header dispatch-detail-header">
                    <div className="dispatch-detail-header-titles">
                        <h2>{t("create_order_title")}</h2>
                        {delivery.name.trim() && (
                            <h3 className="dispatch-detail-header-customer">{delivery.name.trim()}</h3>
                        )}
                    </div>
                    <div className="dispatch-detail-header-meta">
                        <span className="dispatch-status-badge dispatch-status-badge--on-hold">{t("status_on_hold")}</span>
                        <button className="modal-close" onClick={onClose} disabled={saving} aria-label={t("cancel")}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="modal-card-body dispatch-detail-body">
                    {/* Left — lines */}
                    <div className="dispatch-detail-left">
                        <div className="create-order-lines-head">
                            <p className="dispatch-edit-label" style={{ marginTop: 0 }}>{t("order_items")}</p>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setPickerOpen(true)}
                                disabled={saving || readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Plus size={14} />
                                {t("add_product")}
                            </button>
                        </div>
                        <div className="dispatch-detail-items">
                            {lines.length === 0 && (
                                <p className="create-order-empty">{t("create_order_no_lines_yet")}</p>
                            )}
                            {lines.map(line => (
                                <div key={line.sku} className="dispatch-detail-item">
                                    {line.image_url ? (
                                        <img src={line.image_url} alt="" className="dispatch-detail-item-img" />
                                    ) : (
                                        <div className="dispatch-detail-item-img dispatch-detail-item-img--placeholder">
                                            <Package size={16} />
                                        </div>
                                    )}
                                    <div className="dispatch-detail-item-info">
                                        <p className="dispatch-detail-item-title">{line.title}</p>
                                        <p className="dispatch-detail-item-sku">{line.sku}</p>
                                    </div>
                                    <div className="dispatch-detail-item-nums">
                                        <label className="order-line-qty">
                                            <span>{t("qty")}:</span>
                                            <input
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={line.quantity}
                                                aria-label={`${t("qty")} ${line.sku}`}
                                                onChange={e => setLineQty(line.sku, e.target.value)}
                                            />
                                        </label>
                                        <span>{formatMoney(line.unit_price, currency)}</span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-small dispatch-detail-item-map-btn"
                                            onClick={() => removeLine(line.sku)}
                                            aria-label={`${t("remove_line")} ${line.sku}`}
                                            title={t("remove_line")}
                                        >
                                            <Trash2 size={14} />
                                            {t("remove_line")}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="dispatch-detail-totals">
                            <div className="dispatch-detail-totals-row">
                                <span>
                                    {unitCount}{" "}
                                    {unitCount === 1 ? t("item_singular") : t("item_plural")}
                                </span>
                                <span>{formatMoney(total - shippingNum, currency)}</span>
                            </div>
                            <div className="dispatch-detail-totals-row">
                                <span>{t("order_shipping")}</span>
                                <span>{formatMoney(shippingNum, currency)}</span>
                            </div>
                            <div className="dispatch-detail-totals-row dispatch-detail-totals-row--total">
                                <span>{t("order_total")}</span>
                                <span>{formatMoney(total, currency)}</span>
                            </div>
                        </div>
                        <p className="dispatch-edit-hint create-order-hold-note">{t("create_order_hold_note")}</p>
                    </div>

                    {/* Right — order fields + address */}
                    <div className="dispatch-detail-right">
                        <div className="dispatch-order-meta-card">
                            <div className="dispatch-order-meta-fields">
                                <div>
                                    <label className="dispatch-edit-label" htmlFor="create-order-warehouse">{t("warehouse")}</label>
                                    <select
                                        id="create-order-warehouse"
                                        className="dispatch-delivery-type-select"
                                        value={warehouse}
                                        onChange={e => setWarehouse(e.target.value)}
                                    >
                                        {warehouses.length === 0 && <option value="">—</option>}
                                        {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="dispatch-edit-label" htmlFor="create-order-delivery-type">{t("delivery_type")}</label>
                                    <select
                                        id="create-order-delivery-type"
                                        className="dispatch-delivery-type-select"
                                        value={deliveryType}
                                        onChange={e => setDeliveryType(e.target.value as "standard" | "priority" | "express" | "collection")}
                                    >
                                        <option value="standard">{t("delivery_type_standard")}</option>
                                        <option value="priority">{t("delivery_type_priority")}</option>
                                        <option value="express">{t("delivery_type_express")}</option>
                                        <option value="collection">{t("delivery_type_collection")}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="dispatch-order-meta-fields" style={{ marginTop: 8 }}>
                                <div>
                                    <label className="dispatch-edit-label" htmlFor="create-order-number">{t("order_number_optional")}</label>
                                    <input
                                        id="create-order-number"
                                        className="create-order-input"
                                        value={orderNumber}
                                        onChange={e => setOrderNumber(e.target.value)}
                                        placeholder={t("order_number_auto_hint")}
                                        maxLength={64}
                                    />
                                </div>
                                <div>
                                    <label className="dispatch-edit-label" htmlFor="create-order-shipping">{t("order_shipping")}</label>
                                    <input
                                        id="create-order-shipping"
                                        className="create-order-input"
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        inputMode="decimal"
                                        value={shipping}
                                        onChange={e => setShipping(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <label className="dispatch-edit-label" htmlFor="create-order-notes">{t("order_notes_optional")}</label>
                                <textarea
                                    id="create-order-notes"
                                    className="create-order-input"
                                    rows={2}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    maxLength={4000}
                                />
                            </div>
                        </div>

                        <div className="dispatch-address-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={addressTab === "delivery"}
                                className={`dispatch-address-tab ${addressTab === "delivery" ? "dispatch-address-tab--active" : ""}`}
                                onClick={() => setAddressTab("delivery")}
                            >
                                {t("delivery_address")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={addressTab === "billing"}
                                className={`dispatch-address-tab ${addressTab === "billing" ? "dispatch-address-tab--active" : ""}`}
                                onClick={() => setAddressTab("billing")}
                            >
                                {t("billing_address")}
                            </button>
                        </div>
                        <div className="dispatch-address-form dispatch-address-form--compact">
                            <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                                {addressTab === "delivery" ? (
                                    <AddressFields value={delivery} onChange={setDelivery} idPrefix="create-delivery" />
                                ) : (
                                    <>
                                        <label className="create-order-billing-same">
                                            <input
                                                type="checkbox"
                                                checked={billingSame}
                                                onChange={e => {
                                                    setBillingSame(e.target.checked);
                                                    // Start the billing form from the delivery
                                                    // details the first time it is unticked.
                                                    if (!e.target.checked && missingDeliveryFields(billing).length === 5) {
                                                        setBilling({ ...delivery });
                                                    }
                                                }}
                                            />
                                            {t("billing_same_as_delivery")}
                                        </label>
                                        {!billingSame && (
                                            <AddressFields value={billing} onChange={setBilling} idPrefix="create-billing" />
                                        )}
                                    </>
                                )}
                            </fieldset>
                        </div>

                        {fieldError && (
                            <p className="dispatch-replacement-error" role="alert">{fieldError}</p>
                        )}

                        <div className="modal-confirm-buttons dispatch-modal-actions" style={{ marginTop: 12 }}>
                            <button className="btn btn-ghost dispatch-modal-btn" onClick={onClose} disabled={saving}>
                                {t("cancel")}
                            </button>
                            <button
                                className="btn btn-success dispatch-modal-btn"
                                onClick={() => { void save(); }}
                                disabled={saving || readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <CheckCircle strokeWidth={3} size={10} />
                                {saving ? t("submitting") : t("create_order")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {pickerOpen && (
                <OrderProductPicker
                    warehouse={warehouse || undefined}
                    committedQty={sku => lines.find(l => l.sku === sku)?.quantity ?? 0}
                    onPick={addLine}
                    onClose={() => setPickerOpen(false)}
                />
            )}
        </div>
    );
};

export default CreateOrderModal;
